import { Resizable } from "re-resizable";
import { useContext, useEffect, useRef, useState } from "react";
import { RadialGauge } from "canvas-gauges";
import "../../../../styles/Widgets/AnalogueGauges/RadialGaugeWidget.css";
import JwtDecodeService from "../../../../services/JwtDecodeService";
import { AppContext } from "../../../AppProvider";
import { thingsBoardMicroserviceUrl } from "../../../../microservices/thingsboard_microservice";
import { useParams } from "react-router-dom";
import { color } from "chart.js/helpers";

const RadialGaugeWidget = ({DashboardContext, widget, setWidgets, bounds, setDraggingOrResizing, MOBILE_COMPUTER_SCREEN_BOUNDARY }) =>{
    const {
        editMode,setActiveDashboardState,
        activeEditWidget,setActiveEditWidget
    } = useContext(DashboardContext);
    const {user,customerIotProfile,logout} = useContext(AppContext);
    const {tenantId,customerAdminId,customerUserId} = useParams();
    const [device,setDevice] = useState(null);
    const [latestTelemetry,setLatestTelemetry] = useState(null);
    const { gridCellSize } = bounds;
    const [resizing,setResizing] = useState(false);
    const [dragging,setDragging] = useState(false);
    const [newWidgetSize,setNewWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const [widgetPosition, setWidgetPosition] = useState({ col: widget.col, row: widget.row });
    const [widgetSize, setWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const resizableRef = useRef(null);
    const mouseMoveRef = useRef(null);
    const mouseUpRef = useRef(null);

    const fetchDeviceById = async(deviceId) =>{
        try{
            const decodedAccessToken = JwtDecodeService(user?.access_token);
            const defaultAuthority = decodedAccessToken && decodedAccessToken["user-authorities"].length > 0? decodedAccessToken["user-authorities"][0] : 'NONE';
            let url;
            if(defaultAuthority === 'TENANT_ADMIN') url = `${thingsBoardMicroserviceUrl}/tenants/profiles/${tenantId}/devices/${deviceId}`;
            else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'ADMIN') url = `${thingsBoardMicroserviceUrl}/customers/users/admin/${customerAdminId}/devices/${deviceId}`;
            else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'USER') url = `${thingsBoardMicroserviceUrl}/customers/users/user/${customerUserId}/devices/${deviceId}`;
            const response = await fetch(url,{
                method:'GET',
                headers:{
                    'Authorization':`Bearer ${user.access_token}`,
                    'Content-Type':'application/json'
                }
            });
            if(response.ok){
                const responseData = await response.json();
                setDevice(responseData);
            }
            else if(Number(response.status) === 401) logout();
            else throw new Error(`Response code: ${response.status}`);
        }
        catch(error){
            console.error(error);
        }
    };
    const extractRefreshRate = () =>{
        const refreshRateString = widget?.config?.dataSource?.refreshRate;
        if (!refreshRateString) return 5000;
        const regex = /(\d+)\s*(second|seconds|minute|minutes)/i;
        const match = refreshRateString.match(regex);
        if (!match) return 5000;
        const value = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        const seconds = unit.startsWith("minute") ? value * 60 : unit.startsWith("second") ? value : 5;
        return seconds * 1000;
    };
    const formatLastUpdateDisplay= () =>{
        if(!latestTelemetry?.timeStamp) return 'Indefinite';
        const lastUpdateTimeDifference = Date.now() - latestTelemetry?.timeStamp;
        const seconds = Math.floor(lastUpdateTimeDifference / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        if (seconds < 60) return 'Just now';
        if (minutes < 2) return `${minutes} minute ago`;
        if (minutes < 3) return `${minutes} minutes ago`;
        if (minutes < 30) return `${minutes} minutes ago`;
        if (hours < 2) return '1 hour ago';
        if (hours < 24) return `${hours} hours ago`;
        if (days < 2) return '1 day ago';
        if (days < 3) return `${days} days ago`;
        const lastUpdateDate = new Date(latestTelemetry?.timeStamp);
        const formattedDate = `${lastUpdateDate.getMonth() + 1}/${lastUpdateDate.getDate()}/${lastUpdateDate.getFullYear()}`;
        return `${formattedDate}`;
    };

    const position = { 
        x: widgetPosition.col * (gridCellSize + bounds.colGap) + bounds.colGap,
        y: widgetPosition.row * (gridCellSize + bounds.rowGap) + bounds.rowGap
    };
    const size = { 
        width: widgetSize.width * gridCellSize,
        height: widgetSize.height * gridCellSize
    };
    const screenIsMobileScreen = () =>{
        return bounds.width < MOBILE_COMPUTER_SCREEN_BOUNDARY;
    };
    const handleMouseDown = (e) => {
        if(!editMode || resizing || screenIsMobileScreen()) return;
        if(resizableRef.current?.resizableElement?.contains(e.target)) return;

        setDragging(true);
        const startX = e.clientX - position.x;
        const startY = e.clientY - position.y;

        const handleMouseMove = (e) => {
            let newCol = Math.round((e.clientX - startX) / gridCellSize);
            let newRow = Math.round((e.clientY - startY) / gridCellSize);

            newCol = Math.max(0, Math.min(Math.floor(bounds.width / gridCellSize) - widgetSize.width, newCol));
            newRow = Math.max(0, Math.min(Math.floor(bounds.height / gridCellSize) - widgetSize.height, newRow));

            setWidgetPosition({ col: newCol, row: newRow });
        };

        const handleMouseUp = () => {
            setDragging(false);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        mouseMoveRef.current = handleMouseMove;
        mouseUpRef.current = handleMouseUp;
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };
    const handleResizing = (e,direction,ref) =>{
        const newWidth = ref.clientWidth;
        const newHeight = ref.clientHeight;
        setNewWidgetSize({
            width:newWidth,
            height:newHeight
        });
        if(gaugeRef.current){
            gaugeRef.current.update({
                width:newWidth,
                height:newHeight * 0.8
            });
            gaugeRef.current.draw();
        }
    };
    const exportWidget = () =>{
        const blob = new Blob([JSON.stringify(widget,null,2)],{type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement("a"), {href: url, download: `${widget.type}_${widget?.config?.title}.json`});
        a.click();
        URL.revokeObjectURL(url);
    };
    const removeWidget = () =>{
        setActiveDashboardState(prev =>({
            ...prev,widgets:prev?.widgets?.filter(w => w.id !== widget?.id)
        }));
    };
    useEffect(() =>{
        setWidgetSize(prev => ({
            width: Math.round(prev.width),
            height: Math.round(prev.height),
        }));

        setWidgetPosition(prev => ({
            col: Math.round(prev.col),
            row: Math.round(prev.row),
        }));
    },[gridCellSize]);
    useEffect(() =>{
        setDraggingOrResizing(dragging || resizing);
    },[resizing,dragging]);
    useEffect(() =>{
        if(screenIsMobileScreen()) return;
        setNewWidgetSize({
            width:widgetSize.width * gridCellSize,
            height:widgetSize.height * gridCellSize
        });
        setWidgets((prevWidgets) => prevWidgets.map((prevWidget) =>(
            prevWidget.id === widget.id? {
                ...prevWidget,
                row: widgetPosition.row,
                col: widgetPosition.col,
                sizeX: widgetSize.width,
                sizeY: widgetSize.height
            }: prevWidget
        )));
    },[widgetPosition,widgetSize]);
    //end region

    const canvasRef = useRef(null);
    const gaugeRef = useRef(null);
    useEffect(() => {
        const minValue = Number(widget?.config?.data?.minimumValue || 0);
        const maxValue = Number(widget?.config?.data?.maximumValue || 100);
        const majorTickCount = 11;
        gaugeRef.current = new RadialGauge({
            renderTo: canvasRef.current,
            width:widgetSize?.width * gridCellSize,
            height:widgetSize?.height * gridCellSize * 0.8,
            minValue:minValue,
            maxValue:maxValue,
            minorTicks:widget?.config?.appearance?.minorTicks || 10,
            majorTicks:Array.from({ length: majorTickCount }, (_, i) => (minValue + (i * (maxValue - minValue) / (majorTickCount -1))).toString()),
            title:widget?.config?.data?.measurementType,
            value:latestTelemetry?.value || 0,
            valueDec:widget?.config?.data?.decimalPlaces || 0,
            valueInt:0,
            units:widget?.config?.data?.units?.symbol,
            needleType: widget?.config?.appearance?.needleType || "arrow",
            needleWidth:widget?.config?.appearance?.needleWidth,
            needleCircleSize:widget?.config?.appearance?.needleCircleSize,
            colorNeedle:widget?.config?.appearance?.needleColor,
            colorNeedleEnd:widget?.config?.appearance?.needleEndColor,
            highlights:(widget?.config?.appearance?.highlights || []).map(highlight => ({
                from:(highlight?.from / 100) * (maxValue - minValue) + minValue,
                to:(highlight?.to / 100) * (maxValue - minValue) + minValue,
                color:highlight?.color
            }))
        });
        gaugeRef.current.draw();
    },[]);
    useEffect(() =>{
        fetchDeviceById(widget?.config?.dataSource?.deviceId);
        const intervalId = setInterval(() => {
            fetchDeviceById(widget?.config?.dataSource?.deviceId);
        },extractRefreshRate());
        return () => clearInterval(intervalId);
    },[]);
    useEffect(() =>{
        setLatestTelemetry(device?.latestTelemetry.find(item => item.key === widget?.config?.dataSource?.dataKey?.key));
    },[device]);
    useEffect(() =>{
        if(latestTelemetry && gaugeRef.current){
            gaugeRef.current.update({
                value:latestTelemetry?.value,
            });
            gaugeRef.current.draw();
        }
    },[latestTelemetry]);
    return(
        <div className={`general-widget-parent-container ${editMode? (activeEditWidget?.id === widget?.id? 'selected-edit':activeEditWidget? 'active-edit':'passive-edit'):''}`}
            style={{
                position: "absolute",
                left: position.x,
                top: position.y,
                cursor: dragging ? "grabbing" : "default"
            }}
            onClick={() =>{
                if(!activeEditWidget) return;
                setActiveEditWidget(widget);
            }}
            onMouseDown={handleMouseDown}>
            <div className={`general-widget-active-edit-mask ${editMode && activeEditWidget && activeEditWidget?.id !== widget?.id? 'active':''}`}></div>
            <Resizable
                ref={resizableRef}
                size={size}
                minWidth={3 * gridCellSize}
                minHeight={3 * gridCellSize}
                enable={{ right: editMode && !activeEditWidget, bottom: editMode && !activeEditWidget, bottomRight: editMode && !activeEditWidget }}
                onResizeStart={(e) => {
                    e.stopPropagation();
                    setResizing(true);
                }}
                onResize={(e, direction, ref) =>handleResizing(e,direction,ref)}
                onResizeStop={(e,direction,ref) => {
                    setWidgetSize({
                        width: Math.round(ref.clientWidth / gridCellSize),
                        height: Math.round(ref.clientHeight / gridCellSize),
                    });
                    setResizing(false);
                }}>
                    <div className="general-widget-container" style={{borderRadius:`${widget?.borderRadius}%`}} onMouseDown={handleMouseDown}>
                        <div className="general-widget-edit-controls-container"
                            style={{display:editMode && !activeEditWidget? 'flex':'none'}}>
                            <div onClick={() => setActiveEditWidget(widget)}>
                                <p className="edit-widget-p">&#9997;</p>
                            </div>
                            <div onClick={() => exportWidget()}>
                                <p className="export-widget-p">&#128229;</p>
                            </div>
                            <div onClick={() => removeWidget()}>
                                <p className="remove-widget-p">&#10006;</p>
                            </div>
                        </div>
                        <div className="radial-gauge-widget-container">
                            <div className="radial-gauge-widget-tool-tip"
                                style={{top:`${widgetSize?.height * (screenIsMobileScreen()? (widget?.mobileHeightFactor || 4)/2 : 12)}px`}}>
                                <div>
                                    <p>Last update <span>{formatLastUpdateDisplay()}</span></p>
                                </div>
                            </div>
                            <div className="radial-gauge-widget-header">
                                <p style={{
                                    visibility:widget?.config?.appearance?.showTitle? 'visible':'hidden',
                                    color:widget?.config?.appearance?.titleColor}}>{widget?.config?.appearance?.title}
                                </p>
                            </div>
                            <div className="radial-gauge-widget-body">
                                <canvas ref={canvasRef}></canvas>
                            </div>
                        </div>
                    </div>
                </Resizable>
        </div>
    );
};
export default RadialGaugeWidget;